import React, { useState, useEffect } from 'react';
import { 
  ChefHat, 
  ArrowLeft, 
  Star, 
  Clock, 
  Utensils,
  Search,
  Download,
  Sparkles,
  Heart,
  Zap,
  Award,
  Flame
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OnlineMenu = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState(['all']);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Fetch menu items from Firestore or use fallback data
    const fetchMenuItems = async () => {
      try {
        // This would normally fetch from Firestore
        // For now, using comprehensive fallback data
        const fallbackItems = [
          // Main Course
          {
            id: '1',
            name: 'Chicken Biryani',
            description: 'Aromatic basmati rice cooked with tender chicken pieces and traditional spices',
            category: 'main-course',
            price: { half: 180, full: 320 },
            image: 'https://images.unsplash.com/photo-1631515243349-e0cb75fb8d3a?w=400&h=300&fit=crop&crop=center',
            rating: 4.5,
            prepTime: '25-30 mins',
            isPopular: true,
            isVeg: false,
            spiceLevel: 'Medium',
            ingredients: ['Chicken', 'Basmati Rice', 'Onions', 'Spices', 'Yogurt']
          },
          {
            id: '2',
            name: 'Chicken Tikka Masala',
            description: 'Grilled chicken pieces cooked in a rich, creamy, and spicy tomato gravy',
            category: 'main-course',
            price: { half: 200, full: 350 },
            image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=300&fit=crop&crop=center',
            rating: 4.6,
            prepTime: '30-35 mins',
            isPopular: true,
            isVeg: false,
            spiceLevel: 'Medium',
            ingredients: ['Chicken', 'Tomatoes', 'Cream', 'Spices', 'Onions']
          },
          {
            id: '3',
            name: 'Dal Makhani',
            description: 'Creamy black lentils cooked with butter, cream, and aromatic spices',
            category: 'main-course',
            price: { half: 120, full: 200 },
            image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=300&fit=crop&crop=center',
            rating: 4.4,
            prepTime: '20-25 mins',
            isPopular: false,
            isVeg: true,
            spiceLevel: 'Mild',
            ingredients: ['Black Lentils', 'Butter', 'Cream', 'Spices', 'Tomatoes']
          },
          {
            id: '4',
            name: 'Paneer Butter Masala',
            description: 'Soft paneer cubes in a rich, creamy tomato and butter gravy',
            category: 'main-course',
            price: { half: 150, full: 250 },
            image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=300&fit=crop&crop=center',
            rating: 4.3,
            prepTime: '20-25 mins',
            isPopular: true,
            isVeg: true,
            spiceLevel: 'Mild',
            ingredients: ['Paneer', 'Tomatoes', 'Butter', 'Cream', 'Spices']
          },
          // Appetizers
          {
            id: '5',
            name: 'Chicken Pakora',
            description: 'Crispy fried chicken fritters, marinated in spices and gram flour batter',
            category: 'appetizers',
            price: { half: 120, full: 200 },
            image: 'https://images.unsplash.com/photo-1563379091339-03246963d4d4?w=400&h=300&fit=crop&crop=center',
            rating: 4.1,
            prepTime: '15-20 mins',
            isPopular: false,
            isVeg: false,
            spiceLevel: 'Medium',
            ingredients: ['Chicken', 'Gram Flour', 'Spices', 'Oil']
          },
          {
            id: '6',
            name: 'Samosa',
            description: 'Crispy triangular pastries filled with spiced potatoes and peas',
            category: 'appetizers',
            price: { half: 40, full: 70 },
            image: 'https://images.unsplash.com/photo-1563379091339-03246963d4d4?w=400&h=300&fit=crop&crop=center',
            rating: 4.2,
            prepTime: '10-15 mins',
            isPopular: true,
            isVeg: true,
            spiceLevel: 'Medium',
            ingredients: ['Potatoes', 'Peas', 'Spices', 'Wheat Flour']
          },
          {
            id: '7',
            name: 'Chicken Wings',
            description: 'Spicy marinated chicken wings grilled to perfection',
            category: 'appetizers',
            price: { half: 150, full: 250 },
            image: 'https://images.unsplash.com/photo-1563379091339-03246963d4d4?w=400&h=300&fit=crop&crop=center',
            rating: 4.0,
            prepTime: '20-25 mins',
            isPopular: false,
            isVeg: false,
            spiceLevel: 'Hot',
            ingredients: ['Chicken Wings', 'Spices', 'Oil', 'Garlic']
          },
          // Desserts
          {
            id: '8',
            name: 'Gulab Jamun',
            description: 'Soft and spongy milk dumplings soaked in sweet rose-flavored syrup',
            category: 'desserts',
            price: { half: 80, full: 140 },
            image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&h=300&fit=crop&crop=center',
            rating: 4.3,
            prepTime: '10-15 mins',
            isPopular: false,
            isVeg: true,
            spiceLevel: 'None',
            ingredients: ['Milk Powder', 'Sugar', 'Rose Water', 'Cardamom']
          },
          {
            id: '9',
            name: 'Ras Malai',
            description: 'Soft cottage cheese dumplings in sweetened milk with cardamom',
            category: 'desserts',
            price: { half: 100, full: 180 },
            image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&h=300&fit=crop&crop=center',
            rating: 4.4,
            prepTime: '15-20 mins',
            isPopular: true,
            isVeg: true,
            spiceLevel: 'None',
            ingredients: ['Cottage Cheese', 'Milk', 'Sugar', 'Cardamom', 'Saffron']
          },
          {
            id: '10',
            name: 'Kheer',
            description: 'Traditional rice pudding with nuts and cardamom',
            category: 'desserts',
            price: { half: 60, full: 100 },
            image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&h=300&fit=crop&crop=center',
            rating: 4.1,
            prepTime: '20-25 mins',
            isPopular: false,
            isVeg: true,
            spiceLevel: 'None',
            ingredients: ['Rice', 'Milk', 'Sugar', 'Nuts', 'Cardamom']
          },
          // Beverages
          {
            id: '11',
            name: 'Mango Lassi',
            description: 'Refreshing yogurt drink blended with sweet mango pulp',
            category: 'beverages',
            price: { half: 60, full: 100 },
            image: 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=400&h=300&fit=crop&crop=center',
            rating: 4.2,
            prepTime: '5-8 mins',
            isPopular: true,
            isVeg: true,
            spiceLevel: 'None',
            ingredients: ['Mango', 'Yogurt', 'Sugar', 'Cardamom']
          },
          {
            id: '12',
            name: 'Masala Chai',
            description: 'Traditional spiced tea with aromatic spices and milk',
            category: 'beverages',
            price: { half: 30, full: 50 },
            image: 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=400&h=300&fit=crop&crop=center',
            rating: 4.0,
            prepTime: '5-8 mins',
            isPopular: true,
            isVeg: true,
            spiceLevel: 'Mild',
            ingredients: ['Tea Leaves', 'Milk', 'Spices', 'Sugar']
          },
          {
            id: '13',
            name: 'Fresh Lime Soda',
            description: 'Refreshing lime soda with mint and spices',
            category: 'beverages',
            price: { half: 40, full: 70 },
            image: 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=400&h=300&fit=crop&crop=center',
            rating: 3.9,
            prepTime: '3-5 mins',
            isPopular: false,
            isVeg: true,
            spiceLevel: 'Mild',
            ingredients: ['Lime', 'Soda', 'Mint', 'Salt', 'Sugar']
          }
        ];

        setMenuItems(fallbackItems);
        const uniqueCategories = ['all', ...new Set(fallbackItems.map(item => item.category))];
        setCategories(uniqueCategories);
        setIsLoaded(true);
      } catch (error) {
        console.error('Error fetching menu items:', error);
        setIsLoaded(true);
      }
    };

    fetchMenuItems();
  }, []);

  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (category) => {
    const categoryNames = {
      'all': 'All Items',
      'main-course': 'Main Course',
      'appetizers': 'Appetizers',
      'desserts': 'Desserts',
      'beverages': 'Beverages'
    };
    return categoryNames[category] || category;
  };

  const getSpiceLevelColor = (level) => {
    const colors = {
      'None': 'bg-gray-100 text-gray-600',
      'Mild': 'bg-green-100 text-green-600',
      'Medium': 'bg-yellow-100 text-yellow-600',
      'Hot': 'bg-red-100 text-red-600'
    };
    return colors[level] || 'bg-gray-100 text-gray-600';
  };

  const handleDownloadMenu = () => {
    // Create a simple text version of the menu for download
    const menuText = menuItems.map(item => 
      `${item.name} - ₹${item.price.half}/${item.price.full}\n${item.description}\n\n`
    ).join('');
    
    const blob = new Blob([menuText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'btech-bawarchi-menu.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #fef3e2 0%, #fed7aa 50%, #fde68a 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-160px',
          right: '-160px',
          width: '320px',
          height: '320px',
          background: 'linear-gradient(135deg, #fed7aa 0%, #fecaca 100%)',
          borderRadius: '50%',
          opacity: 0.2,
          animation: 'pulse 3s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-160px',
          left: '-160px',
          width: '320px',
          height: '320px',
          background: 'linear-gradient(135deg, #fde68a 0%, #fed7aa 100%)',
          borderRadius: '50%',
          opacity: 0.2,
          animation: 'pulse 3s ease-in-out infinite 1.5s'
        }}></div>
      </div>

      {/* Header */}
      <header style={{
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '16px 20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <button
                onClick={() => navigate('/landing')}
                style={{
                  padding: '8px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <ArrowLeft size={24} color="#6b7280" />
              </button>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
                  padding: '8px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                }}>
                  <ChefHat size={24} color="white" />
                </div>
                <div>
                  <h1 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1f2937',
                    margin: 0
                  }}>B.Tech Bawarchi</h1>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: 0
                  }}>Food Menu</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleDownloadMenu}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
              }}
            >
              <Download size={16} />
              <span style={{ fontSize: '14px', fontWeight: '600' }}>Download Menu</span>
            </button>
          </div>
        </div>
      </header>

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px 20px'
      }}>
        {/* Search and Filter */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          padding: '24px',
          marginBottom: '32px',
          transition: 'all 1s ease',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(20px)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            gap: '20px',
            alignItems: window.innerWidth < 768 ? 'stretch' : 'center'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ position: 'relative' }}>
                <Search 
                  size={20} 
                  color="#f97316" 
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                />
                <input
                  type="text"
                  placeholder="Search dishes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    paddingLeft: '44px',
                    paddingRight: '16px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#f97316';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              justifyContent: window.innerWidth < 768 ? 'center' : 'flex-start'
            }}>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '14px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: selectedCategory === category 
                      ? 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)'
                      : '#f3f4f6',
                    color: selectedCategory === category ? 'white' : '#6b7280',
                    boxShadow: selectedCategory === category 
                      ? '0 4px 12px rgba(249, 115, 22, 0.3)'
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategory !== category) {
                      e.currentTarget.style.backgroundColor = '#e5e7eb';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategory !== category) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  {getCategoryName(category)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth < 768 ? '1fr' : window.innerWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: '24px'
        }}>
          {filteredItems.map((item, index) => (
            <div 
              key={item.id} 
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(8px)',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                animationDelay: `${index * 100}ms`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
              }}
            >
              {/* Image */}
              <div style={{
                position: 'relative',
                height: '200px',
                overflow: 'hidden'
              }}>
                <img
                  src={item.image}
                  alt={item.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease'
                  }}
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/400x300/ff6b35/ffffff?text=${encodeURIComponent(item.name)}`;
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                />
                {item.isPopular && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Flame size={12} />
                    <span>Popular</span>
                  </div>
                )}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(8px)',
                  padding: '6px 8px',
                  borderRadius: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Star size={12} color="#f59e0b" fill="#f59e0b" />
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>
                    {item.rating}
                  </span>
                </div>
                {/* Gradient overlay */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.2), transparent)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }}></div>
              </div>

              {/* Content */}
              <div style={{ padding: '20px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1f2937',
                    margin: 0,
                    flex: 1
                  }}>{item.name}</h3>
                  <div style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '10px',
                    fontWeight: '600',
                    backgroundColor: item.isVeg ? '#dcfce7' : '#fecaca',
                    color: item.isVeg ? '#166534' : '#991b1b',
                    marginLeft: '8px'
                  }}>
                    {item.isVeg ? 'VEG' : 'NON-VEG'}
                  </div>
                </div>

                <p style={{
                  color: '#6b7280',
                  fontSize: '14px',
                  marginBottom: '16px',
                  lineHeight: '1.4',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>{item.description}</p>

                {/* Price */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1f2937'
                  }}>
                    ₹{item.price.half} / ₹{item.price.full}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#6b7280'
                  }}>
                    <Clock size={16} />
                    <span style={{ fontSize: '12px' }}>{item.prepTime}</span>
                  </div>
                </div>

                {/* Spice Level */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: item.spiceLevel === 'None' ? '#f3f4f6' : 
                                   item.spiceLevel === 'Mild' ? '#dcfce7' :
                                   item.spiceLevel === 'Medium' ? '#fef3c7' : '#fecaca',
                    color: item.spiceLevel === 'None' ? '#6b7280' :
                           item.spiceLevel === 'Mild' ? '#166534' :
                           item.spiceLevel === 'Medium' ? '#92400e' : '#991b1b'
                  }}>
                    Spice Level: {item.spiceLevel}
                  </div>
                </div>

                {/* Ingredients */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>Key Ingredients:</h4>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px'
                  }}>
                    {item.ingredients.slice(0, 3).map((ingredient, index) => (
                      <span key={index} style={{
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        padding: '2px 6px',
                        borderRadius: '8px',
                        fontSize: '10px'
                      }}>
                        {ingredient}
                      </span>
                    ))}
                    {item.ingredients.length > 3 && (
                      <span style={{
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        padding: '2px 6px',
                        borderRadius: '8px',
                        fontSize: '10px'
                      }}>
                        +{item.ingredients.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Order Button */}
                <button
                  onClick={() => navigate('/order')}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
                  }}
                >
                  <span>Order Now</span>
                  <Zap size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredItems.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '48px 0'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(8px)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Utensils size={32} color="white" />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#6b7280',
                marginBottom: '8px'
              }}>No dishes found</h3>
              <p style={{
                color: '#9ca3af',
                marginBottom: '16px'
              }}>Try adjusting your search or filter criteria</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                style={{
                  background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnlineMenu;
